import React, { useState, useEffect } from 'react';
import { Search, Eye, Calendar, User, TrendingUp } from 'lucide-react';
import { apiService } from '../../services/api';

interface Paciente {
  _id: string;
  usuario: {
    nome: string;
    email: string;
  };
}

interface AvaliacaoFisica {
  _id: string;
  createdAt: string;
  avaliacaoFisica: {
    peso: number;
    altura: number;
    imc: number;
    circunferencias: {
      cintura: number;
      quadril: number;
      braco: number;
    };
    percentualGordura: number;
    massaMuscular: number;
    pressaoArterial: string;
    frequenciaCardiaca: number;
    observacoes: string;
  };
}

const ConsultarAvaliacaoFisica: React.FC = () => {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState<string>('');
  const [avaliacoes, setAvaliacoes] = useState<AvaliacaoFisica[]>([]);
  const [avaliacaoSelecionada, setAvaliacaoSelecionada] = useState<AvaliacaoFisica | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarPacientes();
  }, []);

  const carregarPacientes = async () => {
    try {
      const data = await apiService.getPacientes();
      setPacientes(data);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      // Mock data para desenvolvimento
      setPacientes([
        {
          _id: '1',
          usuario: { nome: 'Maria Silva', email: 'maria@email.com' }
        },
        {
          _id: '2', 
          usuario: { nome: 'João Santos', email: 'joao@email.com' }
        }
      ]);
    }
  };

  const carregarAvaliacoes = async (pacienteId: string) => {
    try {
      setLoading(true);
      const data = await apiService.getAvaliacoes(pacienteId, 'fisica');
      setAvaliacoes(data);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      // Mock data
      setAvaliacoes([
        {
          _id: '1',
          createdAt: '2024-10-15T10:00:00Z',
          avaliacaoFisica: {
            peso: 75.5,
            altura: 165,
            imc: 27.7,
            circunferencias: {
              cintura: 85,
              quadril: 98,
              braco: 32
            },
            percentualGordura: 28.5,
            massaMuscular: 54.0,
            pressaoArterial: '120/80',
            frequenciaCardiaca: 72,
            observacoes: 'Paciente apresenta boa disposição'
          }
        },
        {
          _id: '2',
          createdAt: '2024-09-15T10:00:00Z',
          avaliacaoFisica: {
            peso: 77.2,
            altura: 165,
            imc: 28.4,
            circunferencias: {
              cintura: 87,
              quadril: 100,
              braco: 33
            },
            percentualGordura: 30.1,
            massaMuscular: 52.5,
            pressaoArterial: '125/85',
            frequenciaCardiaca: 75,
            observacoes: 'Primeira avaliação'
          }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const renderAvaliacoesContent = () => {
    if (loading) {
      return (
        <div className="text-center py-3">
          <output className="spinner-border spinner-border-sm text-success">
            <span className="visually-hidden">Carregando...</span>
          </output>
        </div>
      );
    }

    if (avaliacoes.length === 0) {
      return (
        <div className="text-center text-muted py-3">
          <Search size={32} className="mb-2" />
          <p>Nenhuma avaliação encontrada</p>
        </div>
      );
    }

    return (
      <div className="list-group">
        {avaliacoes.map(avaliacao => (
          <button
            key={avaliacao._id}
            className={`list-group-item list-group-item-action ${avaliacaoSelecionada?._id === avaliacao._id ? 'active' : ''}`}
            onClick={() => visualizarAvaliacao(avaliacao)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">
                  <Calendar size={16} className="me-2" />
                  {new Date(avaliacao.createdAt).toLocaleDateString('pt-BR')}
                </h6>
                <p className="mb-1 small">
                  Peso: {avaliacao.avaliacaoFisica.peso}kg | IMC: {avaliacao.avaliacaoFisica.imc?.toFixed(1)}
                </p>
              </div>
              <Eye size={16} />
            </div>
          </button>
        ))}
      </div>
    );
  };

  const handlePacienteChange = (pacienteId: string) => {
    setPacienteSelecionado(pacienteId);
    setAvaliacaoSelecionada(null);
    if (pacienteId) {
      carregarAvaliacoes(pacienteId);
    } else {
      setAvaliacoes([]);
    }
  };

  const visualizarAvaliacao = (avaliacao: AvaliacaoFisica) => {
    setAvaliacaoSelecionada(avaliacao);
  };

  const calcularEvolucao = () => {
    if (avaliacoes.length < 2) return null;
    
    const atual = avaliacoes[0];
    const anterior = avaliacoes[1];
    
    return {
      peso: atual.avaliacaoFisica.peso - anterior.avaliacaoFisica.peso,
      imc: atual.avaliacaoFisica.imc - anterior.avaliacaoFisica.imc,
      gordura: atual.avaliacaoFisica.percentualGordura - anterior.avaliacaoFisica.percentualGordura
    };
  };

  const evolucao = calcularEvolucao();

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">📊 Consultar Avaliações Físicas</h5>
            </div>
            <div className="card-body">
              {/* Seleção de Paciente */}
              <div className="mb-3">
                <label htmlFor="patient-select-avaliacao" className="form-label">Selecionar Paciente</label>
                <select 
                  id="patient-select-avaliacao"
                  className="form-select"
                  value={pacienteSelecionado}
                  onChange={(e) => handlePacienteChange(e.target.value)}
                >
                  <option value="">Escolha um paciente...</option>
                  {pacientes.map(paciente => (
                    <option key={paciente._id} value={paciente._id}>
                      {paciente.usuario.nome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Evolução Rápida */}
              {evolucao && (
                <div className="card bg-light mb-3">
                  <div className="card-body p-3">
                    <h6 className="card-title">
                      <TrendingUp size={16} className="me-2" />
                      Evolução
                    </h6>
                    <div className="row text-center">
                      <div className="col-4">
                        <div className={`text-${evolucao.peso <= 0 ? 'success' : 'danger'}`}>
                          <strong>{evolucao.peso > 0 ? '+' : ''}{evolucao.peso.toFixed(1)}kg</strong>
                        </div>
                        <small className="text-muted">Peso</small>
                      </div>
                      <div className="col-4">
                        <div className={`text-${evolucao.imc <= 0 ? 'success' : 'danger'}`}>
                          <strong>{evolucao.imc > 0 ? '+' : ''}{evolucao.imc.toFixed(1)}</strong>
                        </div>
                        <small className="text-muted">IMC</small>
                      </div>
                      <div className="col-4">
                        <div className={`text-${evolucao.gordura <= 0 ? 'success' : 'danger'}`}>
                          <strong>{evolucao.gordura > 0 ? '+' : ''}{evolucao.gordura.toFixed(1)}%</strong>
                        </div>
                        <small className="text-muted">Gordura</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Avaliações */}
              {pacienteSelecionado && (
                <div>
                  <h6 className="mb-3">Avaliações Registradas</h6>
                  {renderAvaliacoesContent()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          {avaliacaoSelecionada ? (
            <div className="card shadow-sm">
              <div className="card-header bg-light">
                <div className="d-flex align-items-center">
                  <User size={20} className="me-2 text-success" />
                  <div>
                    <h5 className="mb-0">
                      {pacientes.find(p => p._id === pacienteSelecionado)?.usuario.nome}
                    </h5>
                    <small className="text-muted">
                      Avaliação Física de {new Date(avaliacaoSelecionada.createdAt).toLocaleDateString('pt-BR')}
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Medidas Básicas */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="text-success mb-3">Medidas Básicas</h6>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card bg-light">
                      <div className="card-body text-center p-3">
                        <h4 className="text-success mb-1">{avaliacaoSelecionada.avaliacaoFisica.peso}kg</h4>
                        <small className="text-muted">Peso</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card bg-light">
                      <div className="card-body text-center p-3">
                        <h4 className="text-info mb-1">{avaliacaoSelecionada.avaliacaoFisica.altura}cm</h4>
                        <small className="text-muted">Altura</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card bg-light">
                      <div className="card-body text-center p-3">
                        <h4 className="text-warning mb-1">{avaliacaoSelecionada.avaliacaoFisica.imc?.toFixed(1)}</h4>
                        <small className="text-muted">IMC</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card bg-light">
                      <div className="card-body text-center p-3">
                        <h4 className="text-danger mb-1">{avaliacaoSelecionada.avaliacaoFisica.pressaoArterial}</h4>
                        <small className="text-muted">Pressão</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Circunferências */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="text-success mb-3">Circunferências (cm)</h6>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-label">Cintura</div>
                    <div className="form-control bg-light">{avaliacaoSelecionada.avaliacaoFisica.circunferencias.cintura}cm</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-label">Quadril</div>
                    <div className="form-control bg-light">{avaliacaoSelecionada.avaliacaoFisica.circunferencias.quadril}cm</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-label">Braço</div>
                    <div className="form-control bg-light">{avaliacaoSelecionada.avaliacaoFisica.circunferencias.braco}cm</div>
                  </div>
                </div>

                {/* Composição Corporal */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="text-success mb-3">Composição Corporal</h6>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-label">% Gordura</div>
                    <div className="form-control bg-light">{avaliacaoSelecionada.avaliacaoFisica.percentualGordura}%</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-label">Massa Muscular</div>
                    <div className="form-control bg-light">{avaliacaoSelecionada.avaliacaoFisica.massaMuscular}kg</div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-label">Freq. Cardíaca</div>
                    <div className="form-control bg-light">{avaliacaoSelecionada.avaliacaoFisica.frequenciaCardiaca}bpm</div>
                  </div>
                </div>

                {/* Observações */}
                <div className="row">
                  <div className="col-12">
                    <h6 className="text-success">Observações</h6>
                    <p className="text-muted">
                      {avaliacaoSelecionada.avaliacaoFisica.observacoes || 'Nenhuma observação registrada'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <Search size={64} className="text-muted mb-3" />
                <h5 className="text-muted">Selecione um paciente e uma avaliação</h5>
                <p className="text-muted">Escolha um paciente na lista ao lado para visualizar suas avaliações físicas</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultarAvaliacaoFisica;